import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Save, AlertCircle, CheckCircle2, Receipt } from "lucide-react";
import { fmtEur2 } from "@/lib/format";

const REGIMI = [
  { v: "RF01", l: "RF01 - Ordinario" },
  { v: "RF19", l: "RF19 - Forfettario (art. 1, cc. 54-89, L. 190/2014)" },
];

export default function Settings() {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stamps, setStamps] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [p, s] = await Promise.all([api.get("/profile"), api.get("/stamp-duty/summary").catch(()=>({data:null}))]);
        const data = p.data || {};
        // Ensure the Regime select has a real backing value — otherwise Radix
        // treats clicks on the visually-selected option as no-ops and the user
        // can never save. Default to RF01 (Ordinario) when server returns blank.
        if (!data.sdi_regime_fiscale) data.sdi_regime_fiscale = "RF01";
        if (data.default_iva_pct == null) data.default_iva_pct = 22;
        setForm(data);
        setStamps(s.data);
      } catch (e) { toast.error(errMsg(e, "Errore caricamento")); }
      finally { setLoading(false); }
    })();
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const canSave = (form.company_name || (form.company_first_name && form.company_last_name))
    && (form.company_vat || form.company_tax_code)
    && form.sdi_regime_fiscale
    && (form.company_sdi_code || form.company_pec);

  const save = async () => {
    if (!canSave) return toast.error("Compila i campi obbligatori (con asterisco)");
    if (form.company_sdi_code && form.company_sdi_code.length !== 7) {
      return toast.error("Il Codice Destinatario SDI deve essere di 7 caratteri.");
    }
    setSaving(true);
    try {
      await api.put("/profile", form);
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(errMsg(e, "Salvataggio fallito")); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-zen-ink2">Caricamento…</div>;

  const isForf = form.sdi_regime_fiscale === "RF19";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <span className="zen-label flex items-center gap-2"><Building2 size={12}/> Impostazioni</span>
        <h1 className="mt-2 font-heading text-3xl">Dati azienda / Fatturazione</h1>
        <p className="text-zen-ink2 mt-1">Necessari per generare fatture elettroniche valide per lo SdI.</p>
      </div>

      <div className="zen-card p-6 space-y-5">
        <h2 className="font-heading text-lg">Anagrafica</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Ragione sociale</Label>
            <Input data-testid="sf-company-name" value={form.company_name || ""} onChange={(e)=>set("company_name", e.target.value)}/>
            <p className="text-xs text-zen-ink2 mt-1">Oppure compila Nome + Cognome sotto (persona fisica).</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Nome</Label><Input data-testid="sf-first-name" value={form.company_first_name || ""} onChange={(e)=>set("company_first_name", e.target.value)}/></div>
            <div><Label>Cognome</Label><Input data-testid="sf-last-name" value={form.company_last_name || ""} onChange={(e)=>set("company_last_name", e.target.value)}/></div>
          </div>
          <div><Label>Partita IVA *</Label><Input data-testid="sf-vat" value={form.company_vat || ""} onChange={(e)=>set("company_vat", e.target.value)} placeholder="IT12345678901"/></div>
          <div><Label>Codice Fiscale *</Label><Input data-testid="sf-cf" value={form.company_tax_code || ""} onChange={(e)=>set("company_tax_code", e.target.value)}/></div>
        </div>
      </div>

      <div className="zen-card p-6 space-y-4">
        <h2 className="font-heading text-lg">Sede</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label>Indirizzo</Label><Input data-testid="sf-address" value={form.company_address || ""} onChange={(e)=>set("company_address", e.target.value)} placeholder="Via Roma 1"/></div>
          <div><Label>CAP</Label><Input data-testid="sf-zip" value={form.company_zip || ""} onChange={(e)=>set("company_zip", e.target.value)}/></div>
          <div><Label>Comune</Label><Input data-testid="sf-city" value={form.company_city || ""} onChange={(e)=>set("company_city", e.target.value)}/></div>
          <div><Label>Provincia</Label><Input data-testid="sf-province" value={form.company_province || ""} onChange={(e)=>set("company_province", e.target.value)} placeholder="RM"/></div>
          <div><Label>Nazione</Label><Input data-testid="sf-country" value={form.company_country || "Italia"} onChange={(e)=>set("company_country", e.target.value)}/></div>
        </div>
      </div>

      <div className="zen-card p-6 space-y-4">
        <h2 className="font-heading text-lg">Regime fiscale e SdI</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Regime fiscale *</Label>
            <Select value={form.sdi_regime_fiscale} onValueChange={(v)=>set("sdi_regime_fiscale", v)}>
              <SelectTrigger data-testid="sf-regime"><SelectValue/></SelectTrigger>
              <SelectContent>{REGIMI.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
            </Select>
            {isForf && (
              <div className="mt-2 text-xs text-zen-info bg-zen-info/10 border border-zen-info/30 rounded-lg p-2">
                Regime forfettario: le fatture applicheranno automaticamente natura IVA <b>N2.2</b> con aliquota 0%
                e includeranno la dicitura di legge. La marca da bollo virtuale (€2) viene aggiunta automaticamente
                sopra i €77,47 di imponibile.
              </div>
            )}
          </div>
          <div>
            <Label>Aliquota IVA di default</Label>
            <Select value={String(form.default_iva_pct ?? 22)} onValueChange={(v)=>set("default_iva_pct", parseFloat(v))}>
              <SelectTrigger data-testid="sf-iva"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="22">22% (ordinaria)</SelectItem>
                <SelectItem value="10">10%</SelectItem>
                <SelectItem value="4">4%</SelectItem>
                <SelectItem value="0">0% (esente / non imponibile)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Codice Destinatario SDI (7 caratteri) *</Label>
            <Input data-testid="sf-sdi" maxLength={7} value={form.company_sdi_code || ""} onChange={(e)=>set("company_sdi_code", e.target.value.toUpperCase().slice(0,7))} placeholder="0000000"/>
          </div>
          <div>
            <Label>Oppure PEC *</Label>
            <Input data-testid="sf-pec" type="email" value={form.company_pec || ""} onChange={(e)=>set("company_pec", e.target.value)} placeholder="tuapec@pec.it"/>
          </div>
        </div>
      </div>

      <div className="zen-card p-6 space-y-4">
        <h2 className="font-heading text-lg">Contatti e pagamenti</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>Email pubblica</Label><Input data-testid="sf-email" value={form.company_email || ""} onChange={(e)=>set("company_email", e.target.value)}/></div>
          <div><Label>Telefono</Label><Input data-testid="sf-phone" value={form.company_phone || ""} onChange={(e)=>set("company_phone", e.target.value)}/></div>
          <div className="md:col-span-2"><Label>IBAN</Label><Input data-testid="sf-iban" value={form.company_iban || ""} onChange={(e)=>set("company_iban", e.target.value.toUpperCase())} placeholder="IT60X0542811101000000123456"/></div>
        </div>
      </div>

      {stamps && (
        <div className="zen-card p-6 space-y-3" data-testid="stamp-duty-card">
          <div className="flex items-center gap-2">
            <Receipt size={16} className="text-zen-primary"/>
            <h2 className="font-heading text-lg">Marca da bollo virtuale — {stamps.year}</h2>
          </div>
          <p className="text-sm text-zen-ink2">Totale bollo maturato per il versamento F24 (2€ per fattura forfettaria &gt; €77,47).</p>
          <div className="grid grid-cols-4 gap-3 text-center">
            {["Q1","Q2","Q3","Q4"].map(q => (
              <div key={q} className={`rounded-xl p-3 ${q===stamps.current_quarter ? "bg-zen-primary/10 border border-zen-primary/30" : "bg-zen-bg"}`}>
                <div className="text-xs text-zen-ink2">{q}</div>
                <div className="font-heading text-xl mt-1">{fmtEur2(stamps.quarters[q] || 0)}</div>
              </div>
            ))}
          </div>
          <div className="text-sm text-zen-ink2">Anno {stamps.year}: <b className="text-zen-ink">{fmtEur2(stamps.ytd)}</b> · {stamps.invoices_count} fatture.</div>
        </div>
      )}

      <div className="flex justify-between items-center sticky bottom-4 zen-card p-4 shadow-lg">
        <div className="text-sm">
          {canSave
            ? <span className="text-zen-primary flex items-center gap-1"><CheckCircle2 size={14}/> Dati completi per SdI</span>
            : <span className="text-zen-alert flex items-center gap-1"><AlertCircle size={14}/> Compila i campi *</span>}
        </div>
        <div className="flex gap-2">
          <Link to="/fatture" className="text-sm text-zen-ink2 hover:text-zen-primary self-center">Torna alle fatture</Link>
          <Button data-testid="sf-save" onClick={save} disabled={saving || !canSave} className="bg-zen-primary hover:bg-zen-primaryHover rounded-full disabled:opacity-60">
            <Save size={14} className="mr-1.5"/>{saving ? "Salvo…" : "Salva impostazioni"}
          </Button>
        </div>
      </div>
    </div>
  );
}
